import { Feather } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import { fontSizes } from '_/config/theme';
import { Text } from '_/space-management/components';
import { DATETAB_TIME_FORMAT, DATE_FORMAT, IN_FULL_FORMAT } from '_/space-management/config/date';
import { colors, spacing } from '_/space-management/constants/theme';
import { formatDate } from '_/space-management/helpers/formatDate';
import { useDateContext } from '_/space-management/hooks/DateContext';
import { useEventContext } from '_/space-management/hooks/EventContext';
import { useSpaceContext } from '_/space-management/hooks/SpaceContext';
import { SpaceType } from '_/space-management/services/models/enums/space-type.enum';
import { parse, isSameDay, isBefore } from 'date-fns';
import React, { useMemo } from 'react';
import { TouchableOpacity, View, StyleSheet } from 'react-native';

interface DateSelectorProps {
  spaceType?: string;
}

export default function DateSelector({ spaceType }: DateSelectorProps) {
  const { availableListLoading: loading } = useEventContext();
  const { currentSpace } = useSpaceContext();
  const {
    handleChangeSelectedDay,
    selectedDay,
    selectedEventDate,
    maxDate,
    minDate,
    isDateBlocked,
    timePickerVisible,
  } = useDateContext();
  const navigation = useNavigation<any>();

  const isMinDate = useMemo(() => {
    return selectedDay === formatDate(minDate, DATE_FORMAT);
  }, [selectedDay, minDate]);

  const isMaxDate = useMemo(() => {
    if (!maxDate) return false;
    return selectedDay === formatDate(maxDate as Date, DATE_FORMAT);
  }, [maxDate, selectedDay]);

  const isNextBlocked = useMemo(() => {
    const nextDate = parse(selectedDay, DATE_FORMAT, new Date());
    nextDate.setDate(nextDate.getDate() + 1);
    return (isMaxDate) || isDateBlocked(nextDate);
  }, [selectedDay, isMaxDate, isDateBlocked]);

  const isPrevBlocked = useMemo(() => {
    return isMinDate || loading === 'loadData';
  }, [isMinDate, loading]);

  return (
    <View style={styles.container}>
      <TouchableOpacity
        style={[styles.button, styles.sideButton]}
        disabled={isPrevBlocked}
        onPress={() => handleChangeSelectedDay('sub')}
      >
        <Feather
          name="chevron-left"
          color={isPrevBlocked ? colors.lightGrey : colors.black}
          size={22}
        />
      </TouchableOpacity>
      <TouchableOpacity style={[styles.button, styles.innerButton]} onPress={navigation.goBack}>
        <Text fontSize={fontSizes.md}>
          {formatDate(parse(selectedDay, DATE_FORMAT, new Date()), IN_FULL_FORMAT)}
        </Text>
        {(timePickerVisible || spaceType === SpaceType.WORK) && !currentSpace.scheduleEnabled && (
          <Text fontSize={fontSizes.md} fontType="semibold">
            {formatDate(selectedEventDate.start, DATETAB_TIME_FORMAT)} -{' '}
            {formatDate(selectedEventDate.end, DATETAB_TIME_FORMAT)}
          </Text>
        )}
      </TouchableOpacity>
      <TouchableOpacity
        style={[styles.button, styles.sideButton]}
        onPress={() => handleChangeSelectedDay('add')}
        disabled={isNextBlocked || loading === 'loadData'}
      >
        <Feather
          name="chevron-right"
          color={isNextBlocked || loading === 'loadData' ? colors.lightGrey : colors.black}
          size={22}
        />
      </TouchableOpacity>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    paddingHorizontal: spacing.md,
    paddingBottom: spacing.xxxs,
    paddingTop: spacing.lg,
  },
  button: {
    borderWidth: 1,
    borderColor: colors.white,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 12,
    height: 48,
  },
  innerButton: {
    flex: 1,
    marginHorizontal: spacing.sm,
  },
  sideButton: {
    width: 48,
  },
});
